import React from 'react'
import { useTranslation } from 'react-i18next'

import { AnalysisCommentBlock } from '~/components/features/AnalysisCommentBlock'
import { ChartContainer } from '~/components/features/ChartContainer'
import { ShareDropDownMenuButton } from '~/components/features/ShareDropDownMenuButton'

import {
  useCommentContext,
  useSavedCommentContext,
} from '../providers/AtomsProvider'
import { useFigureInfo } from '../providers/CreateFigureInfoProvider'
import { useRestoringParams } from '../providers/CreateRestoringParamsProvider'
import { type RowData } from '../utils'

import {
  IndividualChartContent,
  type IndividualChartContentProps,
} from './IndividualChartContent'

export type IndividualChartProps<T extends RowData<string>> =
  IndividualChartContentProps<T> & {
    id: string
  }

export const IndividualChart = <T extends RowData<string>>(
  props: IndividualChartProps<T>,
) => {
  const { t } = useTranslation()
  const [image, setImage] = React.useState<HTMLDivElement | null>(null)

  const figureInfo = useFigureInfo(t('graph_1'))

  const id = React.useMemo(() => `individual-chart-${props.id}`, [props.id])

  const restoringParams = useRestoringParams(id)

  const [comment, setComment] = useCommentContext(id)
  const [savedComment, setSavedComment] = useSavedCommentContext(id)

  return (
    <>
      <AnalysisCommentBlock
        savedComment={savedComment}
        saveDocumentImage={
          image
            ? {
                commentReadOnly: true,
                figureInfo,
                image,
                initialComment: comment,
                onSaved: (saved) => setSavedComment(saved),
                restoringParams,
              }
            : undefined
        }
        value={comment}
        onCommentChange={setComment}
      />
      <ChartContainer
        header={
          <>
            {image && (
              <ShareDropDownMenuButton
                downloadAsPng={{ image, name: 'chart' }}
                getSharedUrl={{ restoringParams }}
                saveDocumentImage={{
                  commentReadOnly: true,
                  figureInfo,
                  image,
                  initialComment: comment,
                  onSaved: (saved) => setSavedComment(saved),
                  restoringParams,
                }}
              />
            )}
          </>
        }
        id={id}
      >
        <IndividualChartContent ref={setImage} {...props} />
      </ChartContainer>
    </>
  )
}
